import {
  estimateFeature,
  loadData,
  type Feature,
  type LoadedData,
} from "@fathom/core";
import type { Benchmark } from "@fathom/velocity";
import type { ExtractedItem, EnrichedSlice, VelocityBenchmarkRef } from "./types.js";

/**
 * Convert extracted items to Feature objects for the core estimator.
 */
function toFeature(item: ExtractedItem, index: number): Feature {
  return {
    id: `intake-${index + 1}`,
    name: item.name,
    category: item.category,
    complexity: item.complexity,
    priority: item.priority,
    description: item.description,
    dependencies: [],
    aiTasks: item.aiTasks,
    acceptanceCriteria: item.acceptanceCriteria,
  };
}

/**
 * Find matching velocity benchmark for a given category + complexity.
 */
function findBenchmark(
  benchmarks: Benchmark[],
  category: string,
  complexity: string,
): VelocityBenchmarkRef | undefined {
  // Try exact match first
  const exact = benchmarks.find(
    (b) =>
      b.category.toLowerCase() === category.toLowerCase() &&
      b.complexity === complexity,
  );

  if (exact && exact.dataPoints >= 3) {
    return {
      avgSessions: exact.avgSessions,
      avgDays: exact.avgWallClockDays,
      avgCost: exact.avgCost,
      dataPoints: exact.dataPoints,
    };
  }

  // Fall back to complexity-only match
  const byComplexity = benchmarks.filter((b) => b.complexity === complexity);
  if (byComplexity.length > 0) {
    const avg = (arr: number[]) =>
      arr.reduce((s, v) => s + v, 0) / arr.length;
    const totalPoints = byComplexity.reduce((s, b) => s + b.dataPoints, 0);

    if (totalPoints >= 3) {
      return {
        avgSessions: avg(byComplexity.map((b) => b.avgSessions)),
        avgDays: avg(byComplexity.map((b) => b.avgWallClockDays)),
        avgCost: avg(byComplexity.map((b) => b.avgCost)),
        dataPoints: totalPoints,
      };
    }
  }

  return undefined;
}

export interface EnrichOptions {
  data?: LoadedData;
  benchmarks?: Benchmark[];
  project?: string;
}

/**
 * Enrich extracted items with estimates and velocity benchmarks.
 */
export function enrichSlices(
  items: ExtractedItem[],
  options: EnrichOptions = {},
): EnrichedSlice[] {
  const data = options.data ?? loadData();
  const benchmarks = options.benchmarks ?? [];
  const prefix = options.project ? slugify(options.project) : "intake";

  return items.map((item, index) => {
    const feature = toFeature(item, index);
    const estimate = estimateFeature(feature, data);
    const benchmark = findBenchmark(benchmarks, item.category, item.complexity);

    return {
      sliceId: `${prefix}-${String(index + 1).padStart(3, "0")}`,
      name: item.name,
      category: item.category,
      complexity: item.complexity,
      priority: item.priority,
      description: item.description,
      rootCause: item.rootCause,
      aiTasks: item.aiTasks,
      acceptanceCriteria: item.acceptanceCriteria,
      taskTypes: estimate.taskTypes,
      recommendedModel: estimate.recommendedModel,
      estimatedTokens: estimate.withOverhead.total,
      estimatedCost: estimate.estimatedCost,
      velocityBenchmark: benchmark,
      status: "pending" as const,
    };
  });
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
